<script setup lang="ts">
import {
  matchInquiryTypeToIcon,
  matchInquiryFilterToInquiryType,
  matchInquiryTypeToShowType,
} from '@/utils/matchers'
import { inquiryTypes, showFilterData, showSortData } from '@/constants'
import { INQUIRY_FILTER_OPTIONS } from '@/types'

type FilterDataItem = {
  shortLabel: string
  type: string
  options: INQUIRY_FILTER_OPTIONS[]
  selectedLabel: string
}

const emits = defineEmits(['update:filters'])
const appStore = useAppStore()
const route = useRoute()

const showInquiryFilterData: INQUIRY_FILTER_OPTIONS[] = inquiryTypes.map(
  (type) => ({
    ...matchInquiryTypeToShowType(type),
    icon: matchInquiryTypeToIcon(type),
  }),
)

const showFilterPopUp = ref(false)
const filterPopUpData = shallowRef<FilterDataItem>({
  shortLabel: '',
  type: '',
  options: [],
  selectedLabel: '',
})

//add all inquiries to the beginning of the list
setAllInquiriesFirst()

const filters = shallowRef([
  {
    shortLabel: showInquiryFilterData[0].shortLabel,
    selectedLabel: showInquiryFilterData[0].label,
    type: 'inquiryType',
    options: showInquiryFilterData,
  },
  {
    shortLabel: showFilterData[0].shortLabel,
    selectedLabel: showFilterData[0].label,

    type: 'filter',
    options: showFilterData,
  },
  {
    shortLabel: showSortData[0].shortLabel,
    selectedLabel: showSortData[0].label,

    type: 'sort',
    options: showSortData,
  },
])

const handleButtonFilter = (currentFilter: FilterDataItem) => {
  filterPopUpData.value = currentFilter
  showFilterPopUp.value = true
}

const handlePopUpFilter = (currentFilter: INQUIRY_FILTER_OPTIONS) => {
  let currentItemIndex = filters.value.findIndex(
    (item) => item.type === filterPopUpData.value.type,
  )
  //shallow ref update
  filters.value[currentItemIndex].shortLabel = currentFilter.shortLabel
  filters.value[currentItemIndex].selectedLabel = currentFilter.label

  filters.value = [...filters.value]

  showFilterPopUp.value = false

  emits('update:filters', {
    inquiryType: matchInquiryFilterToInquiryType(filters.value[0].shortLabel),
    filter: filters.value[1].shortLabel,
    sort: filters.value[2].shortLabel,
  })
}

function setAllInquiriesFirst() {
  const itemIndex = showInquiryFilterData.findIndex(
    (item) => item.label === 'All Inquiries' && item.shortLabel === 'All',
  )
  const [item] = showInquiryFilterData.splice(itemIndex, 1)
  item.icon = undefined
  showInquiryFilterData.unshift(item)
}

if (appStore.inquiryFilter) {
  const inquiryTypeFilter = showInquiryFilterData.find(
    (e) => e.shortLabel === appStore.inquiryFilter,
  )!

  filters.value[0] = {
    shortLabel: inquiryTypeFilter.shortLabel,
    selectedLabel: inquiryTypeFilter.label,
    type: 'inquiryType',
    options: showInquiryFilterData,
  }
}

if (route.query.inquiryType) {
  const inquiryTypeFilter = showInquiryFilterData.find(
    (e) =>
      matchInquiryFilterToInquiryType(e.shortLabel) === route.query.inquiryType,
  )!

  const findFilter = showFilterData.find(
    (e) => e.shortLabel === route.query.filter,
  )!

  const findSort = showSortData.find((e) => e.shortLabel === route.query.sort)!

  filters.value[0] = {
    shortLabel: inquiryTypeFilter.shortLabel,
    selectedLabel: inquiryTypeFilter.label,
    type: 'inquiryType',
    options: showInquiryFilterData,
  }

  filters.value[1] = {
    shortLabel: findFilter.shortLabel,
    selectedLabel: findFilter.label,
    type: 'filter',
    options: showFilterData,
  }

  filters.value[2] = {
    shortLabel: findSort.shortLabel,
    selectedLabel: findSort.label,
    type: 'sort',
    options: showSortData,
  }
}
</script>

<template>
  <div class="flex w-full justify-between gap-2 p-2">
    <button
      v-for="filter in filters"
      :key="filter.shortLabel"
      class="flex w-32 items-center justify-between gap-1 truncate rounded-full border border-brand-border px-2 py-2"
      @click="handleButtonFilter(filter)"
    >
      <span class="text-[13px]">{{ filter.shortLabel }}</span>
      <HeroiconsChevronDown class="h-5 w-5 text-black" />
    </button>
  </div>

  <AppDialog
    :is-open="showFilterPopUp"
    :can-close="true"
    :has-close-button="true"
    @close="showFilterPopUp = false"
  >
    <template #title>
      <p
        v-if="filterPopUpData.type == 'inquiryType'"
        class="font-semibold text-gray-500"
      >
        Show inquiry type:
      </p>
      <p
        v-else-if="filterPopUpData.type == 'filter'"
        class="font-semibold text-gray-500"
      >
        Show only:
      </p>
      <p v-else class="font-semibold text-gray-500">Sort inquiries by:</p>
    </template>
    <template #content>
      <AppList
        v-for="option in filterPopUpData.options"
        :key="option.label"
        name="filter"
        :checked="option.label === filterPopUpData.selectedLabel"
        :label="option.label"
        :icon="option.icon"
        @click="handlePopUpFilter(option)"
      />
    </template>
  </AppDialog>
</template>
